import React, { useState, useEffect } from 'react';
import {
  IAssignmentView,
  IRelatedTask,
  ITask,
  TaskDependencyType,
} from 'store/tasks/tasksType';
import { getAllTasks, searchTasks } from 'store/tasks/tasksSlice';
import { appDispatch, RootState } from 'store/store';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import TaskListView, { WindowSize } from 'components/TaskListView/TaskListView';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HoverIcon from '../../../components/HoverIcon/HoverIcon';
import { getPureTextFromHtml } from '../../../store/tasks/taskManipulate';
import { TaskTitleText } from '../../../components/TaskListView/styles';
import TaskUserList from 'components/UserSelector/TaskUserList';
import { IUserOption } from 'components/UserSelector/UserSelector';
import { gray } from 'utils/style/colors';

interface IProps {
  task: ITask;
  editing: boolean;
  companyId?: number;
  handleClickOnTask?: (taskId: number) => void;
  onRelatedTasksChange?: (relatedTasks: IRelatedTask[]) => void;
  onUpdateAssignment?: (assignment: IAssignmentView) => any;
  onUpdateTask: (task: ITask) => void;
  windowSize: WindowSize;
}

const RelatedTasksComponentWrapper = styled.div`
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  & > h6 {
    font-weight: bold;
  }
`;

const RelatedTasksComponent: React.FC<IProps> = ({
  task,
  editing,
  companyId,
  handleClickOnTask,
  onRelatedTasksChange,
  onUpdateTask,
  windowSize,
  onUpdateAssignment,
}) => {
  const { t } = useTranslation('taskManager');

  const tasks = useSelector((state: RootState) => state.tasks.data);
  const users = useSelector((state: RootState) => state.users);

  const [searchOptions, setSearchOptions] = useState<IOption[]>([]);
  const [selectedValues, setSelectedValues] = useState<IOption[]>([]);
  const [relatedTasks, setRelatedTasks] = useState<IRelatedTask[]>(
    task.relatedTasks || []
  );
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [loadingRelatedTasks, setLoadingRelatedTasks] = useState(false);

  useEffect(() => {
    setRelatedTasks(task.relatedTasks || []);
    const currentSelectedValues = (task.relatedTasks || [])
      .map((rt) => ({
        value: rt.taskId.toString(),
        label: tasks?.[rt.taskId]?.textTitle || rt.taskId.toString(),
      }))
      .filter((item) => tasks?.[Number(item.value)]);
    setSelectedValues(currentSelectedValues);
    setSearchOptions(currentSelectedValues);
  }, []);

  useEffect(() => {
    if (onRelatedTasksChange) {
      onRelatedTasksChange(relatedTasks);
    }
  }, [relatedTasks]);

  useEffect(() => {
    const toFetchTasks = relatedTasks
      .filter((it) => !tasks[it.taskId])
      .map((it) => it.taskId);
    if (toFetchTasks.length) {
      setLoadingRelatedTasks(true);
      appDispatch(
        getAllTasks({
          taskIdList: toFetchTasks,
          includeProgressHistory: false,
          includeAssignmentHistory: true,
          includeParentTasks: false,
        })
      )
        .then(
          () => {
            console.log();
          },
          (e: any) => {
            // notifyError(e);
            // error meesage is not clear enough to user undrestand that
            console.log(e);
          }
        )
        .finally(() => {
          setLoadingRelatedTasks(false);
        });
    }
  }, [relatedTasks]);

  const handleSearch = async (searchText: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!searchText || searchText.length < 2) {
      return;
    }

    return new Promise<void>((resolve) => {
      const timeout = setTimeout(async () => {
        try {
          const result = await appDispatch(
            searchTasks({
              params: {
                from: 0,
                count: 20,
                companyId,
                text: searchText,
                archived: false,
                deleteState: 'notDeleted',
              },
            })
          );

          const options = result.tasks
            .filter(
              (t: ITask) =>
                t.id !== task.id &&
                !relatedTasks.some((rt) => rt.taskId === t.id)
            )
            .map((t: ITask) => ({
              value: t.id.toString(),
              label: getPureTextFromHtml(t.title),
            }));

          setSearchOptions(options);
        } catch (error) {
          console.error('Search failed:', error);
        } finally {
          resolve();
        }
      }, 1000);

      setSearchTimeout(timeout);
    });
  };

  const handleAddRelatedTask = (optionsList: IOption[]) => {
    const finalSelectedValues = [...selectedValues, ...optionsList];
    setSelectedValues(finalSelectedValues);

    const currentTaskIds = relatedTasks.map((rt) => rt.taskId.toString());
    const newTaskIds = finalSelectedValues
      .map((opt) => opt.value)

      .filter((id) => !currentTaskIds.includes(id.toString()));

    const selectedTaskIds = finalSelectedValues.map((opt) => opt.value);
    const removedTaskIds = currentTaskIds.filter(
      (id) => !selectedTaskIds.includes(id)
    );

    const newRelatedTasksToAdd = newTaskIds.map((taskId) => ({
      taskId: Number(taskId),
      properties: { dependency: 'NoDependency' as TaskDependencyType },
    }));

    setRelatedTasks((prev) => [
      ...prev.filter((rt) => !removedTaskIds.includes(rt.taskId.toString())),
      ...newRelatedTasksToAdd,
    ]);

    setSearchOptions((prev) =>
      prev.filter(
        (opt) => !finalSelectedValues.map((el) => el.value).includes(opt.value)
      )
    );
  };

  const handleRemoveRelatedTask = (taskId: number) => {
    const updatedRelatedTasks = relatedTasks.filter(
      (rt) => rt.taskId !== taskId
    );
    setRelatedTasks(updatedRelatedTasks);

    const updatedSelectedValues = selectedValues.filter(
      (sv) => sv.value !== taskId.toString()
    );
    setSelectedValues(updatedSelectedValues);
  };

  const handleDependencyChange = (
    taskId: number,
    dependency: TaskDependencyType
  ) => {
    const updatedRelatedTasks = relatedTasks.map((rt) =>
      rt.taskId === taskId
        ? { ...rt, properties: { ...rt.properties, dependency } }
        : rt
    );
    setRelatedTasks(updatedRelatedTasks);
  };

  const dependencyOptions = [
    { value: 'NoDependency', label: t('NoDependency') },
    { value: 'RequireStart', label: t('RequireStart') },
    { value: 'RequireFinish', label: t('RequireFinish') },
  ];

  const relatedTasksData =
    relatedTasks.map(
      (rt) =>
        tasks?.[rt.taskId] ||
        ({
          stub: true,
          title: rt.taskTitle,
          assignee: rt.assignee,
          id: rt.taskId,
        } as any as ITask)
    ) || [];

  const groupedTasks = relatedTasksData.reduce(
    (groups, task) => {
      const relatedTask = relatedTasks.find((rt) => rt.taskId === task.id);
      const dependency = relatedTask?.properties?.dependency || 'NoDependency';
      if (!groups[dependency]) groups[dependency] = [];
      groups[dependency].push(task);
      return groups;
    },
    {} as Record<string, typeof relatedTasksData>
  );

  const handleGenerateAssigneeValues = (assignee: {
    [key: number]: IAssignmentView;
  }) => {
    const assigneeValues = Object.values(assignee || {}).reduce(
      (list: IUserOption[], value) => {
        const item: IUserOption = {
          userId: value.userId,
          name: users.map?.[value.userId]?.name || '',
          roles: [],
          progressDescription: value.progressDescription,
          progress: value.progress,
          optionType: 'user',
          status: value.status ? value.status : 'notStarted',
          relatedRoleId: value.properties?.roleId,
          relatedRoleName: value.properties?.roleName,
        };
        list.push(item);
        return list;
      },
      []
    );
    return assigneeValues;
  };

  return (
    <RelatedTasksComponentWrapper>
      <h6>{t('relatedTasksTitle')}</h6>

      {editing && (
        <Select
          placeholder={t('searchAddRelatedTasks')}
          options={searchOptions}
          onSearch={handleSearch}
          onChangeValues={handleAddRelatedTask}
          multiChoice={false}
          allowRemove={false}
          values={[]}
        />
      )}

      {relatedTasksData.length > 0 && (
        <div>
          {editing
            ? relatedTasksData.map((relatedTask) => {
                const relatedTaskData = relatedTasks.find(
                  (rt) => rt.taskId === relatedTask.id
                );
                return (
                  <div
                    key={relatedTask.id}
                    className="flex flex-nowrap items-center gap-3"
                  >
                    <div className="flex-1">
                      <TaskListView
                        task={relatedTask}
                        template={relatedTask}
                        thisUser={{ userId: 0 } as any}
                        companyId={companyId}
                        thisUserId={0}
                        showProgress={false}
                        onClickOnTask={
                          handleClickOnTask ||
                          (() => {
                            console.log('');
                          })
                        }
                        companyOwnerId={0}
                        onUpdateTask={onUpdateTask}
                        index={0}
                        windowSize={windowSize}
                        disableDroppable={true}
                      />
                    </div>
                    <div className="w-32">
                      <Select
                        options={dependencyOptions}
                        values={[
                          {
                            value:
                              relatedTaskData?.properties?.dependency ||
                              'NoDependency',
                            label: t(
                              relatedTaskData?.properties?.dependency ||
                                'NoDependency'
                            ),
                          },
                        ]}
                        onChangeValues={(options) => {
                          if (options.length > 0) {
                            handleDependencyChange(
                              relatedTask.id,
                              options[0].value as TaskDependencyType
                            );
                          }
                        }}
                        multiChoice={false}
                        allowRemove={false}
                      />
                    </div>
                    <HoverIcon
                      type={'trash-2'}
                      color={'red'}
                      onClick={() => handleRemoveRelatedTask(relatedTask.id)}
                    />
                  </div>
                );
              })
            : Object.entries(groupedTasks).map(([dependency, tasks]) => (
                <div key={dependency} className="mb-4">
                  {loadingRelatedTasks && (
                    <div className="progress">
                      <div className="indeterminate" />
                    </div>
                  )}
                  <h6 className="font-semibold mb-2" style={{ color: gray }}>
                    {t(dependency)}
                  </h6>
                  {tasks.map((relatedTask) => (
                    <div key={relatedTask.id} className="ml-4 w-full my-4">
                      {relatedTask.stub ? (
                        <div className="w-full">
                          <div
                            style={{
                              cursor: 'not-allowed',
                              padding: '0.25rem 1rem',
                              margin: '0',
                              border: `1px dashed ${gray}`,
                              borderRadius: '2rem',
                              fontWeight: '450',
                              color: gray,
                            }}
                          >
                            <TaskTitleText
                              allowedAttributes={{ span: ['style'] }}
                              allowedTags={['span']}
                              html={relatedTask.title}
                            />
                            <TaskUserList
                              trigger="hover"
                              type={task.type}
                              task={task}
                              canChangeProgress={false}
                              canChangeSelfProgress={false}
                              compact={false}
                              showTaskFields={true}
                              values={handleGenerateAssigneeValues(
                                relatedTask.assignee
                              )}
                              disable={true}
                              disablePopover={true}
                            />
                          </div>
                        </div>
                      ) : (
                        <TaskListView
                          task={relatedTask}
                          template={relatedTask}
                          thisUser={{ userId: 0 } as any}
                          companyId={companyId}
                          thisUserId={0}
                          showProgress={false}
                          onClickOnTask={
                            handleClickOnTask ||
                            (() => {
                              console.log('');
                            })
                          }
                          companyOwnerId={0}
                          onUpdateTask={onUpdateTask}
                          index={0}
                          windowSize={windowSize}
                          disableDroppable={true}
                          onUpdateAssignment={onUpdateAssignment}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
        </div>
      )}
    </RelatedTasksComponentWrapper>
  );
};

export default RelatedTasksComponent;
